<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { ArrowDownTrayIcon, ArrowLeftIcon, TrashIcon } from "@heroicons/vue/24/outline";
import { useJobs, type JobDetail } from "../composables/useJobs";
import {
  analysisState,
  displayDone,
  displayStatus,
  mediaAvailability,
  rallySourceRanges,
  retryAffordance,
  sourceSummary,
  type LocalMediaAttachment,
} from "../utils/jobDisplay";
import { isV1Job } from "../utils/inputKind";
import { usePolling } from "../composables/usePolling";
import { useDeleteJob } from "../composables/useDeleteJob";
import { useRetryJob } from "../composables/useRetryJob";
import { useToast } from "../composables/useToast";
import { userMessageForJob } from "../errorMessages";
import StatusBadge from "../components/StatusBadge.vue";
import TraceReference from "../components/TraceReference.vue";
import LoadingSpinner from "../components/LoadingSpinner.vue";
import ConfirmDialog from "../components/ConfirmDialog.vue";
import ProgressPanel from "../components/ProgressPanel.vue";

const props = defineProps<{ id: string }>();

const router = useRouter();
const { fetchJob, loadJobs } = useJobs();
const polling = usePolling();
const { deleteJob, inFlight: deleteInFlight } = useDeleteJob();
const { retryJob, inFlight: retryInFlight } = useRetryJob();
const { showSuccess, showError } = useToast();

// Retry button enable/disable state is owned by segment_api. Optional-field
// fallbacks keep the SPA compatible with an older backend during rollout:
// absence fails open to the endpoint instead of recreating a local cap.
const retryCount = computed(() => job.value?.retry_count ?? 0);
const attemptLabel = computed(() => {
  const limit = job.value?.retry_limit;
  const remaining = job.value?.retries_remaining;
  return limit == null
    ? `Attempt ${retryCount.value + 1}`
    : `Attempt ${retryCount.value + 1} of ${
        remaining == null
          ? limit + 1
          : retryCount.value + Math.max(0, remaining) + 1
      }`;
});
const retryDisabled = computed(
  () => job.value?.can_retry === false || retryInFlight.value,
);
// RallyClip v1: the verified-malformed-bundle failure has no Retry at
// all (hidden, not disabled) — see utils/jobDisplay.ts#retryAffordance.
// Every other failure keeps the existing enabled/disabled button.
const retryShown = computed(
  () => job.value != null && retryAffordance(job.value) === "shown",
);
// Failed-panel copy: the saved input_kind + error_code pair selects the
// v1 detection-file message; everything else is USER_MESSAGES[code].
const failedMessage = computed(() =>
  job.value ? userMessageForJob(job.value) : "",
);
const retryTooltip = computed(() => {
  switch (job.value?.retry_block_reason) {
    case "retry_limit_reached":
      return "Retry limit reached. Contact support if this keeps failing.";
    case "retry_cooldown":
      return "Retry is temporarily paused. Refresh and try again shortly.";
    default:
      return undefined;
  }
});

async function performRetry() {
  if (!job.value) return;
  // Optimistic UI: flip the local status so the failed block disappears
  // and the polling-driven view takes over immediately. The backend
  // transaction is the source of truth — if the POST fails, the next
  // poll tick (≤ POLL_MS away) will flip it back to 'failed'.
  const previousStatus = job.value.status;
  job.value.status = "processing";
  const result = await retryJob(props.id);
  if (result.ok) {
    // Sync the locally-cached retry_count immediately so the badge
    // updates without waiting for the next poll round-trip.
    if (job.value) job.value.retry_count = result.retry_count;
    showSuccess("Retrying job…");
    // The job is now status='processing'; kick the poll cycle. Same
    // shared tick as the initial load — before this was unified, the
    // retry path's inline callback never stopped at terminal status
    // and kept polling until navigation.
    polling.start(pollTick, POLL_MS);
  } else {
    // Roll back the optimistic flip; the row is still failed server-side.
    if (job.value) job.value.status = previousStatus;
    showError(result.error);
  }
}

// Detail view's own dialog open/close state. Decision #8: detail view
// uses a dedicated button (not a kebab) — see delete-job-one-pager.md.
const confirmOpen = ref(false);

function askDelete() {
  confirmOpen.value = true;
}

async function performDelete() {
  if (!job.value) return;
  const jobName = job.value.job_name;
  const result = await deleteJob(props.id);
  confirmOpen.value = false;
  if (result.ok) {
    // Stop polling FIRST — the row is gone; any in-flight poll would
    // 404 next tick and produce a pollError banner during the brief
    // window before the route change.
    polling.stop();
    showSuccess(`Deleted "${jobName}"`);
    router.push({ name: "home" });
  } else {
    // Discriminated union: `result.error: string` is guaranteed
    // when `result.ok` is false — TypeScript narrows it here.
    showError(result.error);
  }
}

const job = ref<JobDetail | null>(null);
const loadError = ref<string | null>(null);
/**
 * Operator-pasteable trace_id companion to ``loadError`` /
 * ``pollError``. Surfaced by ``useJobs.fetchJob`` as
 * ``FetchJobResult.errorTraceId``; rendered via ``<TraceReference>``
 * under the user-facing message. See
 * ``docs/design/error-correlation-one-pager.md`` §A.4.
 */
const loadErrorTraceId = ref<string | undefined>(undefined);
/**
 * `pollError` is separate from `loadError` so a transient hiccup during
 * polling (network blip, brief upstream 5xx) surfaces as a non-destructive
 * "Reconnecting…" banner instead of clobbering a valid last-known-good
 * `job` and dumping the user onto a misleading "Job not found." screen.
 * It also lets the next successful tick clear the banner automatically.
 */
const pollError = ref<string | null>(null);
const pollErrorTraceId = ref<string | undefined>(undefined);
const loaded = ref(false);
const nowMs = ref(Date.now());

const POLL_MS = 5000;

/**
 * Shared poll tick — the initial load and the retry path both start
 * polling with this, so C-W2 (polling stops ONLY at terminal raw
 * status, never at customer_done) and the home-list refresh are
 * enforced in one place instead of drifting across two inline
 * callbacks.
 */
async function pollTick() {
  const wasDisplayDone = job.value != null && displayDone(job.value);
  const tick = await fetchJob(props.id);
  nowMs.value = Date.now();
  if (tick.unauthenticated) {
    polling.stop();
    pollError.value = null;
    pollErrorTraceId.value = undefined;
    return;
  }
  if (!tick.job) {
    pollError.value = tick.error;
    pollErrorTraceId.value = tick.errorTraceId;
    return;
  }
  pollError.value = null;
  pollErrorTraceId.value = undefined;
  job.value = tick.job;
  if (tick.job.status !== "processing") {
    polling.stop();
    // Refresh the home-view list so a status change is reflected
    // when the user navigates back.
    await loadJobs();
  } else if (!wasDisplayDone && displayDone(tick.job)) {
    // customer_done flipped mid-processing: refresh the shared list
    // once so the home-view badge and the tab-title notifier (both
    // watch the jobs ref, which nothing else updates during the
    // finalize tail) see the display transition now, not ~25-40s
    // later at terminal.
    await loadJobs();
  }
}

async function load() {
  const result = await fetchJob(props.id);
  if (result.unauthenticated) {
    // useJobs.apiFetch already called signOut() → App.vue's
    // SIGNED_OUT_EVENT listener will route to /login. Stay in the
    // unloaded state (no `loaded = true`) so the template renders the
    // bare loading spinner during the brief window before the route
    // change — anything else (Session expired., job-not-found) would
    // briefly flash misleading text.
    return;
  }
  job.value = result.job;
  loadError.value = result.error;
  loadErrorTraceId.value = result.errorTraceId;
  pollError.value = null;
  pollErrorTraceId.value = undefined;
  loaded.value = true;

  if (job.value?.status === "processing") {
    polling.start(pollTick, POLL_MS);
  } else {
    polling.stop();
  }
}

onMounted(load);

watch(
  () => props.id,
  () => {
    polling.stop();
    loaded.value = false;
    job.value = null;
    loadError.value = null;
    loadErrorTraceId.value = undefined;
    pollError.value = null;
    pollErrorTraceId.value = undefined;
    void load();
  },
);

/**
 * segment_api may serialize `created_at` as a timezone-naive ISO string
 * (e.g. `"2026-05-15T10:30:00.123456"` — no `Z`, no offset) when the
 * Python side uses `datetime.utcnow()`. `Date.parse` would then treat
 * the string as LOCAL time, producing a `now - startMs` diff of
 * minus-(your-UTC-offset) hours — clamped to 0 by `Math.max`, the
 * elapsed counter sticks at "0m 0s" until enough wall time passes.
 *
 * Detect the missing offset and append `Z` to force UTC parsing.
 */
function parseUtcIso(iso: string): number {
  const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  return Date.parse(hasOffset ? iso : iso + "Z");
}

// Elapsed display moved into <ProgressPanel>; this computed was
// removed when the static "Processing on GPU…" panel was replaced.

/**
 * The status the template branches and the badge render. Maps
 * `processing` + `customer_done` to `completed` so the done view
 * (preview player, no spinner) replaces the progress panel on the
 * same poll tick that delivers `customer_done` — no "100% + spinner"
 * frame is reachable (C-W1). Polling deliberately still keys on the
 * RAW status (C-W2): it must run through the finalize tail to pick
 * up `artifacts.hd` flips and the terminal transition.
 */
const uiStatus = computed(() =>
  job.value ? displayStatus(job.value) : null,
);

// Timeline is a post-done operator artifact: it keeps its
// timeline_path != null gate and appears when it appears (a later
// poll during the finalize tail).
const timelineSrc = computed(() =>
  job.value && uiStatus.value === "completed" && job.value.timeline_path
    ? `/api/jobs/${job.value.job_id}/timeline`
    : null,
);

// --- RallyClip v1 result display (issue #1866, M-13a) ---
//
// Two orthogonal axes drive the done view: the ANALYSIS state (what
// the server checked: rallies / zero rallies / failed) and MEDIA
// availability (what this browser can play). Both are pure
// derivations in utils/jobDisplay.ts so the template never infers
// "done" or "empty" from a highlight URL, `hl_start_s`, or the
// presence of a file — the mistakes device-lifecycle.md "Other
// devices and a missing video" rules out.
const v1 = computed(() => job.value != null && isV1Job(job.value));
const resultState = computed(() =>
  job.value ? analysisState(job.value) : null,
);
// M-13b attachment seam (issue #1867): the follow-on task sets this
// after matching a local file to `job.source`. M-13a never writes it —
// there is no attach control, no hashing, no equivalent-file matching
// here — but the whole media derivation already honours it.
const localMedia = ref<LocalMediaAttachment | null>(null);
function attachLocalMedia(local: LocalMediaAttachment | null) {
  localMedia.value = local;
}
defineExpose({ attachLocalMedia });
const media = computed(() =>
  job.value ? mediaAvailability(job.value, localMedia.value) : "none",
);
// v1 result surfaces: the per-rally source ranges (the server's
// integer rally identities, never re-numbered) and the checked source
// summary (decimal-string byte length formatted without Number()).
const rallyRanges = computed(() =>
  job.value ? rallySourceRanges(job.value) : [],
);
const source = computed(() => (job.value ? sourceSummary(job.value) : null));
// The count prose's "in M:SS of video" tail: v0 keeps video_duration_ms;
// a v1 job has no usage-ledger duration, so the checked playable range
// stands in when the ledger value is null.
const countDurationMs = computed<number | null>(() =>
  job.value?.video_duration_ms ?? source.value?.durationMs ?? null,
);

// Highlights preview source for the inline `<video>` player. Points
// at the 360p variant via the backend's /highlights route — see
// docs/design/highlights-preview-360p-one-pager.md.
//
// The gate is the media axis' `cloud_preview` arm, which is EXACTLY
// the pre-existing rule (utils/jobDisplay.ts#mediaAvailability):
//  - raw status still `processing` → uiStatus is "completed" only via
//    customer_done, which the server derives fail-closed (the 360p
//    PUT succeeded and QA passed, #1092 C-D1) — safe to mount.
//  - terminal `completed` → `highlights_available` stays authoritative,
//    exactly today's gate. customer_done is true for EVERY completed
//    job (incl. legacy pre-360p and QA-failed exports), so it must
//    never bypass the terminal flag; this also keeps pre-#1092
//    backends on today's behavior (C-W6).
//  - empty_result → no preview exists by design; customer_done means
//    analysis is complete, not that a playable 360p was delivered.
//  - v1 → never: no cloud highlights exist for a phone bundle, so an
//    unattached v1 job issues no /highlights fetch at all.
const highlightsHref = computed(() =>
  job.value && media.value === "cloud_preview"
    ? `/api/jobs/${job.value.job_id}/highlights`
    : null,
);

// The player can now mount mid-processing (the window the design
// calls "the brief presign/B2 race") — surface load failures the
// same way HomeView's thumbnail does, so a real outage isn't
// invisible. The next poll tick doesn't remount the element, so this
// is signal-only, not retry.
function onHighlightsError(event: Event) {
  const el = event.target as HTMLVideoElement | null;
  console.warn("highlights preview load failed", el?.src);
}

// HD download affordance — show-or-nothing (C-W5): only the `ready`
// state renders the button; `pending`/`skipped`/`failed`/absent all
// render nothing. HD failure is customer-invisible.
// A v1 job has no cloud HD render either (its artifacts are all
// "skipped"); the explicit kind guard keeps that true even if a
// snapshot ever said otherwise.
const hdHref = computed(() =>
  job.value && !v1.value && job.value.progress?.artifacts?.hd === "ready"
    ? `/api/jobs/${job.value.job_id}/hd`
    : null,
);

import {
  formatBytes,
  formatProcessingTime,
  formatVideoDuration,
} from "../utils/format";

const subLineParts = computed<string[]>(() => {
  // Deliberately the RAW terminal status, not uiStatus: end_to_end_ms
  // is measured at terminal completion, so during the customer_done
  // finalize window there is nothing to show yet.
  if (!job.value || job.value.status !== "completed") return [];
  const parts: string[] = [];
  if (job.value.input_bytes != null) {
    parts.push(`Video size: ${formatBytes(job.value.input_bytes)}`);
  }
  if (job.value.end_to_end_ms != null) {
    parts.push(`Ready in: ${formatProcessingTime(job.value.end_to_end_ms)}`);
  }
  return parts;
});
</script>

<template>
  <section class="p-8 max-w-4xl mx-auto">
    <router-link
      :to="{ name: 'home' }"
      data-testid="back-to-jobs"
      class="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors mb-4"
    >
      <ArrowLeftIcon class="h-4 w-4" aria-hidden="true" />
      Back to jobs
    </router-link>

    <LoadingSpinner
      v-if="!loaded"
      data-testid="job-detail-loading"
      label="Loading…"
      centered
    />

    <div
      v-else-if="loadError || !job"
      data-testid="job-detail-error"
      class="rounded-md border border-error-border bg-error-bg px-4 py-3 text-error"
    >
      <div>{{ loadError ?? "Job not found." }}</div>
      <TraceReference :trace-id="loadErrorTraceId" />
    </div>

    <template v-else>
      <header class="mb-6 flex items-start justify-between gap-4">
        <div class="min-w-0">
          <h2 data-testid="job-title" class="text-2xl font-semibold text-text-primary truncate">
            {{ job.job_name }}
          </h2>
          <p
            v-if="job.video_filename"
            data-testid="job-filename"
            class="text-sm text-text-secondary mt-1 truncate"
          >
            Video: {{ job.video_filename }}
          </p>
          <p
            v-else-if="job.source_url"
            data-testid="job-source-url"
            class="text-sm text-text-secondary mt-1 truncate"
          >
            Source: <a
              :href="job.source_url"
              target="_blank"
              rel="noopener noreferrer"
              class="underline hover:text-text-primary"
            >{{ job.source_url }}</a>
          </p>
          <!-- A v1 job has neither a filename nor a URL: the phone
               analyzed the video and kept the original. -->
          <p
            v-else-if="v1"
            data-testid="job-source-device"
            class="text-sm text-text-secondary mt-1 truncate"
          >
            Video: analyzed on your phone (the original stays on that device)
          </p>
        </div>
        <div class="shrink-0 flex items-center gap-3 mt-1">
          <button
            type="button"
            data-testid="job-detail-delete"
            class="inline-flex items-center gap-1.5 rounded-md border border-error-border bg-bg-main px-3 py-1.5 text-xs font-medium text-error hover:bg-error-bg transition-colors"
            @click="askDelete"
          >
            <TrashIcon class="h-3.5 w-3.5" aria-hidden="true" />
            Delete
          </button>
          <!-- displayStatus-derived: reads "Completed" from
               customer_done onward (C-W1). -->
          <StatusBadge :status="displayStatus(job)" />
        </div>
      </header>

      <ConfirmDialog
        :open="confirmOpen"
        :title="`Delete “${job.job_name}”?`"
        body="This permanently removes the job, the uploaded video, and the rally segmentation output. It can NOT be undone."
        primary-label="Delete forever"
        :in-flight="deleteInFlight"
        @confirm="performDelete"
        @cancel="confirmOpen = false"
      />

      <div
        v-if="pollError"
        data-testid="poll-error"
        class="mb-4 rounded-md border border-warning-border bg-warning-bg px-4 py-2 text-sm text-warning"
      >
        <div>Reconnecting&hellip; ({{ pollError }})</div>
        <TraceReference :trace-id="pollErrorTraceId" />
      </div>

      <div
        v-if="job.status === 'pending_upload'"
        data-testid="status-pending-upload"
        class="rounded-md border border-info-border bg-info-bg px-4 py-3 text-sm text-info"
      >
        <!-- A pending v1 job was submitted from a phone; that device
             finishes the upload. Nothing is expected from this
             browser, so no upload session is started or offered. -->
        <template v-if="v1">
          <div>Waiting for the upload from your phone…</div>
          <p data-testid="pending-upload-remote" class="mt-1 text-info/80">
            Your phone is still sending its analysis. Nothing needs to be
            uploaded from this browser.
          </p>
        </template>
        <template v-else>Waiting for video upload…</template>
      </div>

      <!-- uiStatus, not raw status — see the uiStatus computed.
           ProgressPanel itself is unchanged (C32 95%-cap intact). -->
      <ProgressPanel
        v-else-if="uiStatus === 'processing'"
        :job="job"
        :now-ms="nowMs"
      />

      <div
        v-else-if="uiStatus === 'completed'"
        data-testid="status-completed"
        class="space-y-4"
      >
        <div
          class="rounded-md border border-success-border bg-success-bg px-4 py-3 text-sm text-success"
        >
          <p class="font-medium" data-testid="completed-heading">✓ Completed</p>
          <!-- Count prose renders only when num_segments is known —
               segment_api writes it at terminal reconcile (or the
               C-D3 rescue), so during the customer_done finalize
               window it is null and "Found 0 rallies" would be
               confidently wrong. The heading stands alone until the
               count arrives on a later poll. Two count branches: full
               sentence when we know the input duration, short
               fallback when video_duration_ms is null. Inline
               template bindings (rather than a computed string) so
               the num-segments testid sits on the actual rendered
               count, not on a hidden mirror span. -->
          <p
            v-if="job.num_segments != null && countDurationMs != null"
            class="mt-1"
            data-testid="completed-prose"
          >
            Found
            <span data-testid="num-segments">{{ job.num_segments }}</span>
            {{ job.num_segments === 1 ? "rally" : "rallies" }} in
            {{ formatVideoDuration(countDurationMs) }} of video.
          </p>
          <p
            v-else-if="job.num_segments != null"
            class="mt-1"
            data-testid="completed-prose"
          >
            Found
            <span data-testid="num-segments">{{ job.num_segments }}</span>
            {{ job.num_segments === 1 ? "rally" : "rallies" }}.
          </p>
          <p
            v-if="subLineParts.length > 0"
            class="mt-1 text-success/80 text-xs"
            data-testid="completed-subline"
          >
            {{ subLineParts.join(" · ") }}
          </p>
          <!-- v1: the checked source the phone analyzed. Size comes
               from the decimal-string byte length (never Number()),
               duration from the playable range. -->
          <p
            v-if="source && (source.sizeLabel || source.durationMs != null)"
            class="mt-1 text-success/80 text-xs"
            data-testid="v1-source-summary"
          >
            Original video:
            <template v-if="source.sizeLabel">{{ source.sizeLabel }}</template>
            <template v-if="source.sizeLabel && source.durationMs != null"> · </template>
            <template v-if="source.durationMs != null">{{ formatVideoDuration(source.durationMs) }}</template>
          </p>
        </div>
        <!-- v1 checked zero-rally result: a real, complete analysis
             with nothing to play. Rendered ONLY from the server's
             empty_result — never from a missing file. -->
        <div
          v-if="v1 && resultState === 'completed_empty'"
          data-testid="v1-empty-result"
          class="rounded-md border border-border bg-bg-main px-4 py-3 text-sm text-text-secondary"
        >
          No rallies were detected in this video, so there is nothing to
          play.
        </div>
        <!-- v1 with rallies but no attached file: results are complete
             and shown; only PLAYBACK is unavailable in this browser.
             Not a failure and not "No rallies found". Attaching the
             exact original is the follow-on task (#1867). -->
        <div
          v-if="media === 'unattached'"
          data-testid="playback-unavailable"
          class="rounded-md border border-info-border bg-info-bg px-4 py-3 text-sm text-info"
        >
          <div class="font-medium">Playback isn't available in this browser</div>
          <p class="mt-1 text-info/80">
            The original video stays on the phone that analyzed it and
            isn't attached here. Your rally results are complete and
            listed below.
          </p>
        </div>
        <!-- M-13b seam: an attached local file plays from the same
             source ranges. Unreachable in M-13a (nothing attaches). -->
        <figure v-if="media === 'local_file' && localMedia" class="space-y-1">
          <video
            :src="localMedia.url"
            data-testid="local-media-player"
            controls
            preload="metadata"
            playsinline
            class="w-full rounded-md border border-border bg-bg-main"
          />
          <figcaption class="text-xs text-text-tertiary px-1">
            Attached original video
          </figcaption>
        </figure>
        <!-- v1 rally list: the server's integer rally identities and
             their windows on the original video's clock. -->
        <ol
          v-if="v1 && rallyRanges.length > 0"
          data-testid="v1-rally-list"
          class="divide-y divide-border rounded-md border border-border bg-bg-main text-sm"
        >
          <li
            v-for="range in rallyRanges"
            :key="range.rally"
            :data-testid="`v1-rally-${range.rally}`"
            class="flex items-center justify-between px-4 py-2"
          >
            <span class="font-medium text-text-primary">Rally {{ range.rally }}</span>
            <span class="text-text-secondary tabular-nums">
              {{ formatVideoDuration(range.start_seconds * 1000) }}
              –
              {{ formatVideoDuration(range.end_seconds * 1000) }}
            </span>
          </li>
        </ol>
        <figure v-if="timelineSrc" class="space-y-1">
          <img
            :src="timelineSrc"
            data-testid="timeline-image"
            alt="Rally timeline"
            class="w-full rounded-md border border-border bg-bg-main"
          />
          <figcaption class="text-xs text-text-tertiary px-1">Rally timeline</figcaption>
        </figure>
        <figure v-if="highlightsHref" class="space-y-1">
          <video
            :src="highlightsHref"
            data-testid="highlights-preview"
            controls
            preload="metadata"
            playsinline
            class="w-full rounded-md border border-border bg-bg-main"
            @error="onHighlightsError($event)"
          />
          <!-- Count omitted when num_segments is null — reachable
               mid-finalize if a worker announced preview-ready
               without meta.num_segments; "( rallies)" would render
               confidently broken. -->
          <figcaption
            v-if="job.num_segments != null"
            class="text-xs text-text-tertiary px-1"
          >
            Highlights preview ({{ job.num_segments }}
            {{ job.num_segments === 1 ? "rally" : "rallies" }})
          </figcaption>
          <figcaption v-else class="text-xs text-text-tertiary px-1">
            Highlights preview
          </figcaption>
        </figure>
        <!-- HD download — rendered only at artifacts.hd === "ready"
             (C-W5: show-or-nothing). The route 302s to a presigned
             URL with an attachment disposition. target=_blank so a
             failure response (session expired mid-click, deploy
             skew) opens a throwaway tab instead of replacing the
             SPA with raw JSON; on success the browser downloads and
             auto-closes the tab. -->
        <div v-if="hdHref">
          <a
            :href="hdHref"
            data-testid="hd-download"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-main px-3 py-1.5 text-xs font-medium text-text-primary hover:bg-bg-hover transition-colors"
          >
            <ArrowDownTrayIcon class="h-3.5 w-3.5" aria-hidden="true" />
            Download full-quality video
          </a>
        </div>
      </div>

      <!--
        YouTube ingest failures get a dedicated friendly block with a
        fallback CTA pointing to the file-upload path. URL ingest is
        best-effort per Phase 2 design; users should never see a raw
        yt-dlp error string. See
        docs/design/youtube-url-ingestion-phase-2-one-pager.md.
      -->
      <div
        v-else-if="job.status === 'failed' && job.source_url"
        data-testid="status-failed-youtube"
        class="rounded-md border border-error-border bg-error-bg px-4 py-3 text-sm text-error"
      >
        <div class="font-medium">YouTube couldn't process this video</div>
        <p class="mt-1 text-text-secondary">
          Some videos can't be downloaded directly from YouTube — usually
          because of regional availability, age restrictions, or
          copyright. Try uploading the video file directly instead.
        </p>
        <div class="mt-3">
          <router-link
            :to="{ name: 'new-job' }"
            data-testid="youtube-fallback-upload"
            class="inline-block rounded-md border border-error/40 px-3 py-1 text-xs font-medium text-error hover:bg-error/10"
          >
            Upload file instead
          </router-link>
        </div>
      </div>

      <div
        v-else-if="job.status === 'failed'"
        data-testid="status-failed"
        class="rounded-md border border-error-border bg-error-bg px-4 py-3 text-sm text-error"
      >
        <!--
          Render the user-facing message via the error_code → string
          lookup in errorMessages.ts. NEVER render job.error here —
          that field carries operator-grade content (infra names,
          internal status enums) and is the bug this contract fixes.
          See docs/design/structured-error-reporting-one-pager.md C-S4.
        -->
        <div>Failed: {{ failedMessage }}</div>
        <!--
          Retry block. Per docs/design/retry-failed-jobs-one-pager.md:
          - Server-supplied attempt limit always visible when available.
          - Button disabled when can_retry=false or while a retry POST is in
            flight; retry_block_reason selects the explanation.
          - No confirm dialog (retry is non-destructive).
          RallyClip v1: the verified-malformed-bundle failure renders NO
          retry block at all (retryAffordance === "hidden") — the same
          bytes would fail the same way, and the server 409s the POST.
        -->
        <div
          v-if="retryShown"
          class="mt-3 flex items-center justify-between gap-3"
        >
          <span
            data-testid="retry-attempt-label"
            class="text-xs text-error/80"
          >{{ attemptLabel }}</span>
          <button
            type="button"
            data-testid="retry-button"
            :disabled="retryDisabled"
            :title="retryTooltip"
            class="rounded-md border border-error/40 px-3 py-1 text-xs font-medium text-error hover:bg-error/10 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
            @click="performRetry"
          >
            {{ retryInFlight ? "Retrying…" : "Retry" }}
          </button>
        </div>
      </div>
    </template>
  </section>
</template>
